var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var TextArea_1;
import React, { Component } from 'react';
import { Size } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, eventListener, expression, number, oneOf, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent, FocusEvent, KeyboardEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { MAX_LENGTH } from '@philips-internal/oswald-ui-model/lib/validators/max-length.js';
import { classNames, defaultClasses, isEnumValid, isFunction, isMobile, isUndefined, noop, trigger } from '@philips-internal/oswald-ui-utils';
import { FieldContext } from '../field/context/field-context.js';
import TextAreaAPI from './text-area-api.js';
import './text-area.css';
function Counter({ charactersRemainingExpression = 'tws.text-area.remaining-characters', length = 0, value = '', }) {
    if (!length) {
        return null;
    }
    if (isMobile()) {
        const count = `(${value.length}/${length})`;
        return React.createElement("span", { className: "c-text-area__counter" }, count);
    }
    const remaining = length - value.length;
    const text = `${Math.max(0, remaining)} ${MessageService.translate(charactersRemainingExpression)}`;
    const className = classNames({
        'c-text-area__counter': true,
        'is-invalid': remaining < 0,
    });
    return React.createElement("span", { className: className }, text);
}
Counter.propTypes = {
    charactersRemainingExpression: string,
    length: number,
    value: string,
};
let TextArea = TextArea_1 = class TextArea extends Component {
    constructor() {
        super(...arguments);
        this.getKeyEventObject = (event) => {
            const { value: oldValue } = this.props;
            const { altKey, cancelable, charCode, ctrlKey, currentTarget, key, keyCode, preventDefault, shiftKey, stopPropagation } = event;
            const { value } = currentTarget;
            return { altKey, cancelable, charCode, ctrlKey, key, keyCode, oldValue, preventDefault, shiftKey, stopImmediatePropagation: noop, stopPropagation, value };
        };
        this.updateValidations = (value) => {
            if (!this.context) {
                return;
            }
            const { length } = this.props;
            if (isUndefined(length)) {
                return;
            }
            const validations = ModelService.validate(value).apply(MAX_LENGTH, { max: length }).run();
            this.context.updateChildValidations(validations);
        };
        this.onChange = (event) => {
            const { value: oldValue, onChange } = this.props;
            const { target: { value } } = event;
            this.updateValidations(value);
            isFunction(onChange) && onChange(new ChangeEvent({
                oldValue,
                prettyValue: TextArea_1.formatPrettyValue({ value }),
                value,
            }));
        };
        this.onKeyDown = (event) => {
            const { onKeyDown } = this.props;
            const params = this.getKeyEventObject(event);
            isFunction(onKeyDown) && onKeyDown(new KeyboardEvent(params));
        };
        this.onKeyPress = (event) => {
            const { onKeyPress } = this.props;
            const params = this.getKeyEventObject(event);
            isFunction(onKeyPress) && onKeyPress(new KeyboardEvent(params));
        };
        this.onKeyUp = (event) => {
            const { onKeyUp } = this.props;
            const params = this.getKeyEventObject(event);
            isFunction(onKeyUp) && onKeyUp(new KeyboardEvent(params));
        };
        this.renderFooter = () => {
            const { enabled, length, readOnly, value } = this.props;
            if (!enabled || readOnly || isUndefined(length)) {
                return null;
            }
            return (React.createElement("div", { className: "c-text-area__footer u-hide@view" },
                React.createElement(Counter, { length: length, value: value })));
        };
    }
    /**
     * Prettifies a given value.
     *
     * @param {object} properties - Properties to generate pretty value.
     * @param {string} [properties.value] - Value to be formatted.
     * @returns {string} pretty value.
     */
    static formatPrettyValue({ value }) {
        return MessageService.translate(String(value));
    }
    componentDidMount() {
        const { value = TextArea_1.defaultProps.value } = this.props;
        this.updateValidations(value);
    }
    componentWillUnmount() {
        this.context?.resetChildValidations();
    }
    render() {
        const { className, enabled, name, onBlur, onFocus, placeholder, readOnly, resize, rows, size, valid, value } = this.props;
        const defClasses = defaultClasses({
            enabled: Boolean(enabled),
            readOnly: Boolean(readOnly),
            valid: Boolean(valid),
        });
        const classes = classNames({
            'c-text-area': true,
            'c-text-area--sm': isEnumValid(Size, size) && Size.SMALL === size,
            'c-text-area--validated': !valid,
            ...defClasses,
            [`${className}`]: Boolean(className),
        });
        const style = {
            resize: resize ? 'initial' : 'none',
        };
        // red lines under the text;
        const autoCorrect = readOnly ? 'off' : 'on';
        const spellCheck = !readOnly;
        return (React.createElement("div", { className: classes },
            React.createElement("textarea", { autoCorrect: autoCorrect, className: "c-text-area__input", disabled: !enabled, name: name, onBlur: trigger(onBlur, new FocusEvent()), onChange: this.onChange, onFocus: trigger(onFocus, new FocusEvent()), onKeyDown: this.onKeyDown, onKeyPress: this.onKeyPress, onKeyUp: this.onKeyUp, placeholder: MessageService.translate(placeholder ?? ''), readOnly: readOnly, rows: rows, spellCheck: spellCheck, style: style, value: MessageService.translate(value ?? '') }),
            this.renderFooter()));
    }
};
TextArea.contextType = FieldContext;
TextArea.propTypes = {
    className: string,
    enabled: bool,
    length: number,
    name: string,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    onKeyDown: eventListener,
    onKeyPress: eventListener,
    onKeyUp: eventListener,
    placeholder: expression,
    readOnly: bool,
    required: bool,
    resize: bool,
    rows: number,
    size: oneOf(Object.values(Size)),
    valid: bool,
    value: expression,
};
TextArea.defaultProps = {
    enabled: true,
    name: '',
    placeholder: '',
    readOnly: false,
    required: false,
    resize: false,
    rows: 3,
    size: Size.SMALL,
    valid: true,
    value: '',
};
TextArea = TextArea_1 = __decorate([
    AwesomeComponent({ api: TextAreaAPI, type: ComponentType.DATA_ENTRY })
], TextArea);
export { TextArea, };
export default TextArea;
//# sourceMappingURL=text-area.js.map